import clsx from 'clsx';
import { useAtomValue, useSetAtom } from 'jotai';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentFocusKey, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import styles from './tv-guide.module.scss';
import {
  focusKeyPrevAtom,
  FocusKeyPrevSource,
  tvGuideRecordingStatusUpdatesAtom
} from '@smart-tv/atoms';
import FavoriteFilled from '@smart-tv/components/icons/favorite-icons/favorite-filled';
import RecordingAnimation from '@smart-tv/components/recording-animation/recording-animation';
import Typography from '@smart-tv/components/typography';
import testIds from '@smart-tv/e2e/shared/variables';
import { TVGuideProgramExtended } from '@smart-tv/pages/tv-guide/utils/tv-guide-create-empty-programs';
import isWithinInterval from '@smart-tv/utils/date/is-within-interval';
import formatTitle from '@smart-tv/utils/formatter/format-title';
import addQueryParams from '@smart-tv/utils/routing/add-query-params';
import encodeProgramSheetParams from '@smart-tv/utils/routing/encode-program-sheet-params';

import TvGuideDayViewContext from '../utils/tv-guide-day-view-context';
import { useToast, IconTypes } from '@smart-tv/hooks/use-toast/use-toast';

interface Props {
  style?: React.CSSProperties | undefined;
  program: TVGuideProgramExtended;
  channelIndex: number;
  focusable: boolean;
  onArrowPress: (direction: string) => boolean;
}

const TvGuideProgram: React.FC<Props> = (props: Props) => {
  const { program, style, channelIndex, focusable, onArrowPress } = props;
  const {
    scroll,
    scrollYRef,
    time,
    setActiveProgram,
    activeProgram,
    activeCenter,
    verticalArrowPress,
    setActiveChannelIndex,
    scale,
    scaledConstants
  } = React.useContext(TvGuideDayViewContext);
  const {
    endTime,
    startTime,
    startTimeInMinutes,
    endTimeInMinutes,
    title,
    inactive,
    sid,
    eventId,
    seriesId,
    isFavorite,
    recording,
    trickModes
  } = program;

  const recordingStatusUpdates = useAtomValue(tvGuideRecordingStatusUpdatesAtom);
  const programRecordingStatus = recordingStatusUpdates.programs[`${sid}:${eventId}`]?.isRecording;
  const seriesRecordingStatusUpdate =
    seriesId === undefined ? undefined : recordingStatusUpdates.series[`${sid}:${seriesId}`];
  const seriesRecordingStatus =
    seriesRecordingStatusUpdate?.startTime !== undefined &&
    startTime >= seriesRecordingStatusUpdate.startTime
      ? seriesRecordingStatusUpdate.isRecording
      : undefined;
  const isRecording =
    programRecordingStatus ??
    (seriesRecordingStatus === undefined ? recording : seriesRecordingStatus);
  const showFavoriteIndicator = isFavorite && !isRecording;

  const { showToast } = useToast();

  const textRef = React.useRef<HTMLDivElement>(null);

  const [textWidth, setTextWidth] = React.useState(0);

  const [programWidth, setProgramWidth] = React.useState(0);

  const hasIndicator = isRecording || showFavoriteIndicator;
  const indicatorContainerWidth = hasIndicator ? 32 * scale : 0;

  const minimumTextWidth = 20;

  const necessarySpaceForIndicators = indicatorContainerWidth + 32 * scale;

  const formattedTitle = formatTitle(title);

  const setFocusKeyPrev = useSetAtom(focusKeyPrevAtom);

  const navigate = useNavigate();

  const handleProgramFocus = React.useCallback(() => {
    setActiveProgram(program);
    setActiveChannelIndex(channelIndex);
  }, [channelIndex, program, setActiveChannelIndex, setActiveProgram]);

  const handleProgramMouseEnter = React.useCallback(() => {
    setActiveProgram(program);
  }, [program, setActiveProgram]);

  const handleProgramSelect = React.useCallback(() => {
    if (!inactive) {
      const isAiring = isWithinInterval(Date.now(), {
        start: program.startTime * 1000,
        end: program.endTime * 1000
      });

      setFocusKeyPrev({
        focusKeyPrev: getCurrentFocusKey(),
        isLiveTV: isAiring,
        source: FocusKeyPrevSource.TvGuideAsset
      });

      navigate(
        addQueryParams({
          programsheet: encodeProgramSheetParams(sid, eventId)
        })
      );
    } else {
      showToast('Der findes ingen programinformation', IconTypes.info);
    }
  }, [inactive, navigate, eventId, sid, program, setFocusKeyPrev, showToast]);

  const { ref, focused, focusSelf } = useFocusable({
    focusable: focusable,
    onFocus: handleProgramFocus,
    onEnterPress: handleProgramSelect,
    onArrowPress: onArrowPress,
    focusKey: `${channelIndex}-${program.eventId}`
  });

  const isShortProgram = React.useCallback(() => {
    return endTimeInMinutes - startTimeInMinutes <= 5;
  }, [startTimeInMinutes, endTimeInMinutes]);

  const getTextOffset = React.useCallback(() => {
    return Math.max(
      scroll.left - scaledConstants.gridGap - startTimeInMinutes * scaledConstants.minuteWidth,
      0
    );
  }, [scroll.left, scaledConstants.gridGap, scaledConstants.minuteWidth, startTimeInMinutes]);

  //Calculate the text container width
  React.useEffect(() => {
    if (textRef.current) {
      setTextWidth(textRef.current.offsetWidth);
    }
  }, []);

  //Calculate the program container width
  React.useEffect(() => {
    if (ref.current) {
      setProgramWidth(ref.current.offsetWidth);
    }
  }, [ref]);

  // Focus the program in the active row that intersects with the current active center
  React.useEffect(() => {
    if (verticalArrowPress && focusable && activeCenter !== undefined) {
      const left = startTimeInMinutes * scaledConstants.minuteWidth;
      const right = endTimeInMinutes * scaledConstants.minuteWidth;
      if (left <= activeCenter && activeCenter <= right + scaledConstants.programWGap) {
        focusSelf();
        scrollYRef.current?.scrollTo({
          top:
            channelIndex * (scaledConstants.programHeight + scaledConstants.programGap) -
            scaledConstants.programHeight * 2 -
            scaledConstants.programGap * 2
        });
      }
    }
  }, [
    activeCenter,
    focusSelf,
    focusable,
    ref,
    startTimeInMinutes,
    endTimeInMinutes,
    verticalArrowPress,
    scrollYRef,
    channelIndex,
    scaledConstants.minuteWidth,
    scaledConstants.programWGap,
    scaledConstants.programHeight,
    scaledConstants.programGap
  ]);

  // Calculate available width for the text
  const availableWidth = textWidth - getTextOffset() - indicatorContainerWidth;
  const isHighLighted = endTime > time.unix || trickModes !== 0;
  const isSelected = activeProgram.eventId === eventId && activeProgram.sid === sid;

  return (
    <button
      className={clsx(styles['tv-guide-program'], {
        [styles['tv-guide-program--focused']]: focused,
        [styles['tv-guide-program--highlighted']]: isHighLighted,
        [styles['tv-guide-program--inactive']]: inactive,
        [styles['tv-guide-program--selected']]: isSelected,
        [styles['tv-guide-program--short']]: isShortProgram()
      })}
      style={style}
      data-testid={testIds.tvGuide.grid.programItem}
      ref={ref}
      onClick={handleProgramSelect}
      onMouseEnter={handleProgramMouseEnter}
    >
      {!isShortProgram() && (
        <div
          className={styles['tv-guide-program__container']}
          style={
            {
              '--program-text-offset': getTextOffset() + 'px'
            } as React.CSSProperties
          }
          ref={textRef}
        >
          {availableWidth >= minimumTextWidth && (
            <Typography
              className={styles['tv-guide-program__text']}
              noWrap
              fontWeight="regular"
              style={
                {
                  '--program-text-width':
                    textWidth - getTextOffset() - indicatorContainerWidth + 'px'
                } as React.CSSProperties
              }
            >
              {availableWidth == minimumTextWidth ? '...' : formattedTitle}
            </Typography>
          )}
        </div>
      )}
      {hasIndicator && programWidth - getTextOffset() >= necessarySpaceForIndicators && (
        <div className={styles['tv-guide-program__icons']}>
          {isRecording && <RecordingAnimation className={styles['tv-guide-program__indicator']} />}
          {showFavoriteIndicator && (
            <FavoriteFilled className={styles['tv-guide-program__indicator']} size={false} />
          )}
        </div>
      )}
    </button>
  );
};

export default React.memo(TvGuideProgram);
